import { useCallback, useEffect, useMemo, useState } from 'react';
import type { FC } from 'react';
import type { ColumnsType } from 'antd/es/table';
import { IpcButton, IpcModal, IpcTable } from '../components/ui';
import { nodeAutoCompleteRuleApi } from '../api/services';
import type {
  NodeAutoCompleteRuleRunLogVO,
  NodeAutoCompleteRuleRunNodeLogVO,
} from '../api/types/nodeAutoCompleteRule';
import {
  formatJsonBlock,
  formatRunSource,
  formatRunStatus,
  formatServerDateTime,
  formatTriggerType,
  pickSnakeCamel as pick,
} from '../utils/formatServerDateTime';
import {
  buildProjectScheduleDetailUrl,
  resolveRunNodeLogNavIds,
} from '../utils/projectScheduleNav';
import './NodeAutoCompleteRuleRunLogModal.css';

type Props = {
  open: boolean;
  onClose: () => void;
  ruleId: number;
  ruleName: string;
};

const PAGE_SIZE_OPTIONS = [10, 20, 50];

type DetailField = { label: string; value: string; multiline?: boolean };

function buildNodeDetailFields(row: NodeAutoCompleteRuleRunNodeLogVO): DetailField[] {
  return [
    { label: 'ID', value: String(row.id) },
    { label: '执行批次 ID', value: String(pick(row.run_id, row.runId) ?? '—') },
    { label: '规则 ID', value: String(pick(row.rule_id, row.ruleId) ?? '—') },
    { label: '项目名称', value: pick(row.project_name, row.projectName) || '—' },
    { label: '版本名称', value: pick(row.schedule_name, row.scheduleName) || '—' },
    { label: '规格名称', value: pick(row.spec_name, row.specName) || '—' },
    { label: '节点配置 ID', value: String(pick(row.milestone_node_id, row.milestoneNodeId) ?? '—') },
    { label: '节点名称', value: pick(row.milestone_node_name, row.milestoneNodeName) || '—' },
    {
      label: '排期节点实例 ID',
      value: String(pick(row.schedule_milestone_id, row.scheduleMilestoneId) ?? '—'),
    },
    { label: '执行状态', value: formatRunStatus(row.status) },
    { label: '数据准备状态', value: formatRunStatus(pick(row.prepare_status, row.prepareStatus)) },
    {
      label: '数据准备耗时 (ms)',
      value: String(pick(row.prepare_duration_ms, row.prepareDurationMs) ?? 0),
    },
    {
      label: '数据准备错误',
      value: pick(row.prepare_error_message, row.prepareErrorMessage) || '—',
      multiline: true,
    },
    {
      label: '数据准备入参',
      value: formatJsonBlock(pick(row.prepare_input_json, row.prepareInputJson)),
      multiline: true,
    },
    {
      label: '数据准备出参',
      value: formatJsonBlock(pick(row.prepare_output_json, row.prepareOutputJson)),
      multiline: true,
    },
    { label: '动作决策状态', value: formatRunStatus(pick(row.decision_status, row.decisionStatus)) },
    { label: '决策动作', value: pick(row.decision_action, row.decisionAction) || '—' },
    {
      label: '动作决策耗时 (ms)',
      value: String(pick(row.decision_duration_ms, row.decisionDurationMs) ?? 0),
    },
    {
      label: '动作决策错误',
      value: pick(row.decision_error_message, row.decisionErrorMessage) || '—',
      multiline: true,
    },
    {
      label: '动作决策入参',
      value: formatJsonBlock(pick(row.decision_input_json, row.decisionInputJson)),
      multiline: true,
    },
    {
      label: '动作决策出参',
      value: formatJsonBlock(pick(row.decision_output_json, row.decisionOutputJson)),
      multiline: true,
    },
    { label: '创建时间', value: formatServerDateTime(pick(row.create_time, row.createTime)) },
    { label: '更新时间', value: formatServerDateTime(pick(row.update_time, row.updateTime)) },
  ];
}

const NodeAutoCompleteRuleRunLogModal: FC<Props> = ({ open, onClose, ruleId, ruleName }) => {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [list, setList] = useState<NodeAutoCompleteRuleRunLogVO[]>([]);
  const [detailNode, setDetailNode] = useState<NodeAutoCompleteRuleRunNodeLogVO | null>(null);

  const loadRuns = useCallback(async () => {
    if (!ruleId) return;
    setLoading(true);
    try {
      const res = await nodeAutoCompleteRuleApi.getRuns(ruleId, { pageNum: page, pageSize });
      setList(res.list ?? []);
      setTotal(res.total ?? 0);
    } catch (e) {
      console.error(e);
      setList([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [ruleId, page, pageSize]);

  useEffect(() => {
    if (!open) return;
    setPage(1);
    setDetailNode(null);
  }, [open, ruleId]);

  useEffect(() => {
    if (!open) return;
    void loadRuns();
  }, [open, loadRuns]);

  const parentColumns = useMemo<ColumnsType<NodeAutoCompleteRuleRunLogVO>>(() => [
    {
      title: 'ID',
      key: 'id',
      width: 72,
      render: (_, row) => row.id,
    },
    {
      title: '规则版本',
      key: 'rule_version',
      width: 90,
      render: (_, row) => pick(row.rule_version, row.ruleVersion) ?? 0,
    },
    {
      title: '触发方式',
      key: 'trigger_type',
      width: 100,
      render: (_, row) => formatTriggerType(pick(row.trigger_type, row.triggerType)),
    },
    {
      title: '执行来源',
      key: 'run_source',
      width: 100,
      render: (_, row) => formatRunSource(pick(row.run_source, row.runSource)),
    },
    {
      title: '状态',
      key: 'status',
      width: 90,
      render: (_, row) => formatRunStatus(row.status),
    },
    {
      title: '节点总数',
      key: 'node_total',
      width: 90,
      render: (_, row) => pick(row.node_total, row.nodeTotal) ?? 0,
    },
    {
      title: '成功数',
      key: 'node_success_count',
      width: 80,
      render: (_, row) => pick(row.node_success_count, row.nodeSuccessCount) ?? 0,
    },
    {
      title: '失败数',
      key: 'node_failed_count',
      width: 80,
      render: (_, row) => pick(row.node_failed_count, row.nodeFailedCount) ?? 0,
    },
    {
      title: '耗时 (ms)',
      key: 'duration_ms',
      width: 90,
      render: (_, row) => pick(row.duration_ms, row.durationMs) ?? 0,
    },
    {
      title: '错误信息',
      key: 'error_message',
      ellipsis: true,
      render: (_, row) => pick(row.error_message, row.errorMessage) || '—',
    },
    {
      title: '执行时间',
      key: 'create_time',
      width: 170,
      render: (_, row) => formatServerDateTime(pick(row.create_time, row.createTime)),
    },
  ], []);

  const childColumns = useMemo<ColumnsType<NodeAutoCompleteRuleRunNodeLogVO>>(() => [
    {
      title: 'ID',
      key: 'id',
      width: 72,
      render: (_, row) => row.id,
    },
    {
      title: '项目名称',
      key: 'project_name',
      width: 140,
      ellipsis: true,
      render: (_, row) => {
        const name = pick(row.project_name, row.projectName) || '—';
        const { projectId, scheduleId } = resolveRunNodeLogNavIds(row);
        if (!projectId) return name;
        return (
          <a
            className="acr-rule-run-log-modal__project-link"
            href={buildProjectScheduleDetailUrl(projectId, scheduleId)}
            target="_blank"
            rel="noopener noreferrer"
            title="在新窗口打开项目排期详情"
            onClick={e => e.stopPropagation()}
          >
            {name}
          </a>
        );
      },
    },
    {
      title: '版本名称',
      key: 'schedule_name',
      width: 140,
      ellipsis: true,
      render: (_, row) => pick(row.schedule_name, row.scheduleName) || '—',
    },
    {
      title: '规格名称',
      key: 'spec_name',
      width: 120,
      ellipsis: true,
      render: (_, row) => pick(row.spec_name, row.specName) || '—',
    },
    {
      title: '节点名称',
      key: 'milestone_node_name',
      width: 120,
      ellipsis: true,
      render: (_, row) => pick(row.milestone_node_name, row.milestoneNodeName) || '—',
    },
    {
      title: '数据准备',
      key: 'prepare',
      width: 140,
      render: (_, row) => {
        const st = formatRunStatus(pick(row.prepare_status, row.prepareStatus));
        const ms = pick(row.prepare_duration_ms, row.prepareDurationMs) ?? 0;
        return `${st} · ${ms}ms`;
      },
    },
    {
      title: '动作决策',
      key: 'decision',
      width: 180,
      render: (_, row) => {
        const st = formatRunStatus(pick(row.decision_status, row.decisionStatus));
        const action = pick(row.decision_action, row.decisionAction) || '—';
        const ms = pick(row.decision_duration_ms, row.decisionDurationMs) ?? 0;
        return `${st} · ${action} · ${ms}ms`;
      },
    },
    {
      title: '错误信息',
      key: 'error',
      ellipsis: true,
      render: (_, row) =>
        pick(row.prepare_error_message, row.prepareErrorMessage)
        || pick(row.decision_error_message, row.decisionErrorMessage)
        || '—',
    },
  ], []);

  const detailFields = detailNode ? buildNodeDetailFields(detailNode) : [];

  return (
    <>
      <IpcModal
        open={open}
        title={`执行日志${ruleName.trim() ? ` · ${ruleName.trim()}` : ''}`}
        width={1120}
        onCancel={onClose}
        className="acr-rule-run-log-modal"
        footer={(
          <div className="acr-rule-run-log-modal__footer">
            <IpcButton variant="primary" onClick={onClose}>关闭</IpcButton>
          </div>
        )}
      >
        <div className="acr-rule-run-log-modal__body">
          <IpcTable<NodeAutoCompleteRuleRunLogVO>
            frameless
            rowKey="id"
            loading={loading}
            columns={parentColumns}
            dataSource={list}
            emptyTitle="暂无执行日志"
            emptyDescription="测试或正式触发后将在此展示"
            scroll={{ x: 980 }}
            expandable={{
              expandedRowRender: record => (
                <div className="acr-rule-run-log-modal__child">
                  <IpcTable<NodeAutoCompleteRuleRunNodeLogVO>
                    frameless
                    compact
                    rowKey="id"
                    columns={childColumns}
                    dataSource={record.nodes ?? []}
                    pagination={false}
                    emptyTitle="无节点明细"
                    scroll={{ x: 1180 }}
                    onRow={row => ({
                      onClick: () => setDetailNode(row),
                      className: 'acr-rule-run-log-modal__child-row',
                    })}
                  />
                </div>
              ),
              rowExpandable: record => (record.nodes?.length ?? 0) > 0,
            }}
            pagination={{
              current: page,
              pageSize,
              total,
              pageSizeOptions: PAGE_SIZE_OPTIONS,
              onChange: (nextPage, nextSize) => {
                setPage(nextPage);
                setPageSize(nextSize);
              },
            }}
          />
        </div>
      </IpcModal>

      <IpcModal
        open={Boolean(detailNode)}
        title="节点执行明细"
        width={760}
        onCancel={() => setDetailNode(null)}
        className="acr-rule-run-node-detail-modal"
        footer={(
          <div className="acr-rule-run-log-modal__footer">
            <IpcButton variant="primary" onClick={() => setDetailNode(null)}>关闭</IpcButton>
          </div>
        )}
      >
        <dl className="acr-rule-run-node-detail">
          {detailFields.map(field => (
            <div key={field.label} className="acr-rule-run-node-detail__row">
              <dt>{field.label}</dt>
              <dd>
                {field.multiline ? (
                  <pre className="acr-rule-run-node-detail__pre">{field.value}</pre>
                ) : (
                  field.value
                )}
              </dd>
            </div>
          ))}
        </dl>
      </IpcModal>
    </>
  );
};

export default NodeAutoCompleteRuleRunLogModal;
